import assert from 'assert';
import {produce} from 'immer';
import {v4 as uuidv4} from 'uuid';
import {assign, setup, assertEvent} from 'xstate';

import {PLATE_MAP_MAX_CONCENTRATION_LAYERS, PatternDirection} from 'samples/plate-maps/PlateMapConstants';
import {HydratedUnit} from 'units/util';

/**
 * Dilution settings for a concentration layer
 */
export interface DilutionSettings {
  /** Dilution factor (e.g., 2 for 1:2 dilution) */
  dilutionFactor: number | null;
  /** Direction to apply dilutions */
  dilutionDirection: PatternDirection;
  /** Number of replicates */
  replicates: number | null;
  /** Direction to apply replicates */
  replicateDirection: PatternDirection;
}

/**
 * Represents a single concentration layer's data
 */
export interface ConcentrationLayerData extends ConcentrationSettingsWithDilutionSettings {
  /** Unique identifier for this concentration layer */
  layerId: string;
  /** Name of the concentration layer */
  name: string;
}

export interface ConcentrationSettings {
  /** Concentration value */
  concentration: number | null;
  /** Concentration unit */
  concentrationUnit: HydratedUnit | null;
  /** Whether dilution settings are enabled for this layer */
  allowSetDilutionSettings: boolean;
}

export interface ConcentrationSettingsWithDilutionSettings extends ConcentrationSettings {
  dilutionSettings: DilutionSettings;
}

/**
 * Events for the ConcentrationLayerMachine
 */
export type ConcentrationLayerEvents =
  | {type: 'initLayers'; layers: Array<ConcentrationLayerData>}
  | {type: 'addLayer'; layerId: string; name: string}
  | {type: 'removeLayer'; layerId: string}
  | {type: 'setActiveLayer'; layerId: string | null}
  | {type: 'updateLayerName'; layerId: string; name: string}
  | {
      type: 'updateConcentrationSettings';
      layerId: string;
      settings: Partial<ConcentrationSettings>;
    }
  | {
      type: 'updateLayerDilutionSettings';
      layerId: string;
      settings: Partial<DilutionSettings>;
    };

/**
 * Context for the ConcentrationLayerMachine - now manages multiple layers
 */
export interface ConcentrationLayerContext {
  /** Map of layer ID to layer data */
  layers: Map<string, ConcentrationLayerData>;
  /** Currently active/selected layer for editing */
  activeLayerId: string | null;
}

/**
 * Input for creating a ConcentrationLayerMachine
 */
export interface ConcentrationLayerMachineInput {
  /** Initial layers to create */
  initialLayers?: Array<ConcentrationLayerData>;
  /** Override the active layer instead of defaulting to the first layer */
  activeLayerId?: string | null;
}

export function getDefaultDilutionSettings(): DilutionSettings {
  return {
    dilutionFactor: null,
    dilutionDirection: PatternDirection.RIGHT,
    replicates: null,
    replicateDirection: PatternDirection.RIGHT,
  };
}

/**
 * Helper function to create a new layer with default settings
 */
export function createDefaultLayerSettings(layerId: string, name: string): ConcentrationLayerData {
  const dilutionSettings: DilutionSettings = getDefaultDilutionSettings();

  return {
    layerId,
    name,
    concentration: null,
    concentrationUnit: null,
    allowSetDilutionSettings: false,
    dilutionSettings,
  };
}

export function createConcentrationLayerId(): string {
  return `concentration-layer-${uuidv4()}`;
}

/**
 * State machine for managing multiple concentration layers
 */
export const ConcentrationLayerMachine = setup({
  types: {
    input: {} as ConcentrationLayerMachineInput,
    context: {} as ConcentrationLayerContext,
    events: {} as ConcentrationLayerEvents,
  },
  actions: {
    initLayers: assign(({context, event}) => {
      assertEvent(event, 'initLayers');
      assert(
        event.layers.length <= PLATE_MAP_MAX_CONCENTRATION_LAYERS,
        `Cannot initialize ConcentrationLayerMachine with ${event.layers.length} layers. ` +
          `Maximum allowed is ${PLATE_MAP_MAX_CONCENTRATION_LAYERS}.`
      );

      return {
        layers: new Map(event.layers.map((layer) => [layer.layerId, layer])),
        orderedLayerIds: event.layers.map((layer) => layer.layerId),
        activeLayerId:
          event.layers.find((layer) => layer.layerId === context.activeLayerId) != null
            ? context.activeLayerId
            : null,
      };
    }),

    /**
     * Add a new concentration layer to the machine
     *
     * This action will create a new layer with default settings and add it to the machine.
     * The newly added layer will be set as the active layer.
     */
    addLayer: assign(({context, event}) => {
      assertEvent(event, 'addLayer');
      assert(
        context.layers.size < PLATE_MAP_MAX_CONCENTRATION_LAYERS,
        `Cannot add concentration layer. Maximum allowed is ${PLATE_MAP_MAX_CONCENTRATION_LAYERS}.`
      );
      const newLayer = createDefaultLayerSettings(event.layerId, event.name);

      return produce(context, (draft) => {
        draft.layers.set(newLayer.layerId, newLayer);
        // Auto-select the newly added layer
        draft.activeLayerId = newLayer.layerId;
      });
    }),

    /**
     * Remove a concentration layer from the machine
     *
     * This action will remove the layer from the machine. If the layer being removed is the active layer,
     * the active layer will be set to the first remaining layer in the list, or null if no layers remain.
     */
    removeLayer: assign(({context, event}) => {
      assertEvent(event, 'removeLayer');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        draft.layers.delete(event.layerId);

        // If the removed layer was active, select the first available layer or null
        if (context.activeLayerId === event.layerId) {
          const remainingLayerIds = Array.from(draft.layers.keys());
          draft.activeLayerId = remainingLayerIds[0] ?? null;
        }
      });
    }),

    /**
     * Set the active layer for editing
     */
    setActiveLayer: assign(({context, event}) => {
      assertEvent(event, 'setActiveLayer');

      if (event.layerId != null && !context.layers.has(event.layerId)) {
        return {};
      }

      return produce(context, (draft) => {
        draft.activeLayerId = event.layerId;
      });
    }),

    /**
     * Update the name of a concentration layer
     */
    updateLayerName: assign(({context, event}) => {
      assertEvent(event, 'updateLayerName');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          draftLayer.name = event.name;
        }
      });
    }),

    /**
     * Update concentration settings of a concentration layer
     */
    updateConcentrationSettings: assign(({context, event}) => {
      assertEvent(event, 'updateConcentrationSettings');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          if (event.settings.concentration !== undefined) {
            draftLayer.concentration = event.settings.concentration;
          }

          if (event.settings.concentrationUnit !== undefined) {
            draftLayer.concentrationUnit = event.settings.concentrationUnit;
          }

          if (event.settings.allowSetDilutionSettings !== undefined) {
            draftLayer.allowSetDilutionSettings = event.settings.allowSetDilutionSettings;
          }
        }
      });
    }),

    /**
     * Update the dilution settings of a concentration layer
     */
    updateLayerDilutionSettings: assign(({context, event}) => {
      assertEvent(event, 'updateLayerDilutionSettings');

      const layer = context.layers.get(event.layerId);
      if (!layer || !layer.allowSetDilutionSettings) {
        return {};
      }

      return produce(context, (draft) => {
        const draftLayer = draft.layers.get(event.layerId);
        if (draftLayer) {
          Object.assign(draftLayer.dilutionSettings, event.settings);
        }
      });
    }),
  },
}).createMachine({
  context: ({input}) => {
    if (input.initialLayers == null) {
      return {layers: new Map(), activeLayerId: null};
    }
    assert(
      input.initialLayers.length <= PLATE_MAP_MAX_CONCENTRATION_LAYERS,
      `Cannot initialize ConcentrationLayerMachine with ${input.initialLayers.length} layers. ` +
        `Maximum allowed is ${PLATE_MAP_MAX_CONCENTRATION_LAYERS}.`
    );
    const layerMap = new Map(input.initialLayers.map((layer) => [layer.layerId, layer]));
    let activeLayerId: string | null;
    if (input.activeLayerId !== undefined) {
      assert(
        input.activeLayerId == null || layerMap.has(input.activeLayerId),
        `activeLayerId "${input.activeLayerId}" not found in initialLayers`
      );
      activeLayerId = input.activeLayerId;
    } else {
      activeLayerId = input.initialLayers[0]?.layerId ?? null;
    }
    return {layers: layerMap, activeLayerId};
  },

  id: 'ConcentrationLayer',
  initial: 'idle',

  states: {
    idle: {
      on: {
        initLayers: {
          actions: 'initLayers',
        },
        addLayer: {
          actions: 'addLayer',
        },
        removeLayer: {
          actions: 'removeLayer',
        },
        setActiveLayer: {
          actions: 'setActiveLayer',
        },
        updateLayerName: {
          actions: 'updateLayerName',
        },
        updateConcentrationSettings: {
          actions: 'updateConcentrationSettings',
        },
        updateLayerDilutionSettings: {
          actions: 'updateLayerDilutionSettings',
        },
      },
    },
  },
});
